/**
 * 3D structural frame for the Viewer: beams/girders as merged line segments,
 * columns as instanced boxes, and lateral placeholders as wireframe volumes.
 *
 * This layer is a HYBRID of two ConduitLayer sub-patterns rather than a mirror
 * of any one existing layer: beams are linear (like conduit centerlines, one
 * merged LineSegments per role per storey) while columns are extruded volumes
 * (like pull boxes, one InstancedMesh). `DeviceLayer`'s point-marker model fits
 * neither — a column is not a point and a beam is not a marker.
 *
 * Lateral placeholders render as WIREFRAME boxes, deliberately: they are
 * reserved geometry with no designed system behind them, and a solid volume
 * would read as a shear wall the model never described.
 *
 * A THIN render of the engine's Output — no placement or screening logic here.
 *
 * Coordinates: server data is plan metres (x east, y north, z up); three.js is
 * y-up, so everything maps through planToThree (x, z, y) — same as DuctLayer.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import {
  beamLineVertices,
  memberColor,
  planToThree,
  type LayerBeam,
  type LayerColumn,
  type LayerLateral,
} from "../mep/members";

/** Minimum drawn column footprint (m), so a thin section stays visible. */
const MIN_COLUMN_SIZE = 0.15;

function beamLineGeometry(beam: LayerBeam): THREE.BufferGeometry | null {
  const verts = beamLineVertices(beam);
  if (verts.length === 0) return null;
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(verts, 3));
  return geometry;
}

export class MemberLayer {
  /** Added to the Viewer's scene; everything the layer renders lives here. */
  readonly group = new THREE.Group();

  private columns: LayerColumn[] = [];
  private beams: LayerBeam[] = [];
  private lateral: LayerLateral[] = [];
  private storeyFilter: string | null = null;
  private built: (THREE.LineSegments | THREE.InstancedMesh | THREE.Mesh)[] = [];

  // Shared resources reused across rebuilds (filter/visibility toggles);
  // disposed only in dispose(). Per-rebuild merged/box geometry IS unique and
  // IS disposed by clear() — same split ConduitLayer documents.
  private lineMatCache = new Map<string, THREE.LineBasicMaterial>();
  private solidMatCache = new Map<string, THREE.MeshStandardMaterial>();
  private lateralMat: THREE.MeshBasicMaterial | null = null;
  private unitBox: THREE.BoxGeometry | null = null;

  constructor() {
    this.group.name = "istari-member-layer";
  }

  /** Replace the whole frame (called after generation/regeneration). */
  setMembers(
    columns: readonly LayerColumn[],
    beams: readonly LayerBeam[],
    lateral: readonly LayerLateral[] = [],
  ): void {
    this.columns = [...columns];
    this.beams = [...beams];
    this.lateral = [...lateral];
    this.rebuild();
  }

  /** Toggle the whole member layer (independent of ducts/devices/conduits). */
  setLayerVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  get layerVisible(): boolean {
    return this.group.visible;
  }

  /**
   * Restrict members to one storey (`null` shows all).
   *
   * Lateral placeholders span storeys by construction, so they are NOT filtered
   * out by a storey selection — hiding a multi-storey element while viewing one
   * of the storeys it passes through would misrepresent the frame.
   */
  setStoreyFilter(storeyId: string | null): void {
    this.storeyFilter = storeyId;
    this.rebuild();
  }

  /** Names of the meshes currently built, sorted (deterministic; tests/UI). */
  meshNames(): string[] {
    return this.built.map((m) => m.name).sort();
  }

  /** Rendered instance count for one instanced mesh name (0 if absent). */
  instanceCount(name: string): number {
    const mesh = this.built.find((m) => m.name === name);
    return mesh instanceof THREE.InstancedMesh ? mesh.count : 0;
  }

  private passesFilter(storeyId: string): boolean {
    if (this.storeyFilter === null) return true;
    return storeyId === this.storeyFilter;
  }

  private rebuild(): void {
    this.clear();
    this.buildBeams();
    this.buildColumns();
    this.buildLateral();
  }

  /** One merged LineSegments geometry per role per storey. */
  private buildBeams(): void {
    const groups = new Map<string, THREE.BufferGeometry[]>();
    for (const beam of this.beams) {
      if (!this.passesFilter(beam.storey_id)) continue;
      const geometry = beamLineGeometry(beam);
      if (!geometry) continue;
      const key = `beams:${beam.role}:${beam.storey_id}`;
      const list = groups.get(key) ?? [];
      list.push(geometry);
      groups.set(key, list);
    }
    for (const key of [...groups.keys()].sort()) {
      const parts = groups.get(key)!;
      const merged = mergeGeometries(parts, false);
      for (const part of parts) part.dispose();
      if (!merged) continue;
      const role = key.split(":")[1];
      const mesh = new THREE.LineSegments(merged, this.cachedLineMaterial(role));
      mesh.name = key;
      this.add(mesh);
    }
  }

  /**
   * One InstancedMesh of column volumes per storey.
   *
   * A column is scaled from a unit box so a single shared geometry serves every
   * section size — the per-instance matrix carries both the footprint and the
   * storey height.
   */
  private buildColumns(): void {
    const byStorey = new Map<string, LayerColumn[]>();
    for (const col of this.columns) {
      if (!this.passesFilter(col.storey_id)) continue;
      const list = byStorey.get(col.storey_id) ?? [];
      list.push(col);
      byStorey.set(col.storey_id, list);
    }
    if (byStorey.size === 0) return;
    this.unitBox ??= new THREE.BoxGeometry(1, 1, 1);
    const matrix = new THREE.Matrix4();
    for (const storey of [...byStorey.keys()].sort()) {
      const list = byStorey.get(storey)!;
      const mesh = new THREE.InstancedMesh(
        this.unitBox,
        this.cachedSolidMaterial("column"),
        list.length,
      );
      mesh.name = `columns:${storey}`;
      list.forEach((col, i) => {
        const size = Math.max(col.size_m, MIN_COLUMN_SIZE);
        const height = Math.max(col.height_m, MIN_COLUMN_SIZE);
        // Box is centred, so the centre sits half a storey above the base.
        const [x, y, z] = planToThree([
          col.x_m,
          col.y_m,
          col.base_z_m + height / 2,
        ]);
        matrix.compose(
          new THREE.Vector3(x, y, z),
          new THREE.Quaternion(),
          // three.js y is up, so the storey height scales y.
          new THREE.Vector3(size, height, size),
        );
        mesh.setMatrixAt(i, matrix);
      });
      mesh.instanceMatrix.needsUpdate = true;
      this.add(mesh);
    }
  }

  /** Wireframe volumes for reserved lateral geometry (see module docs). */
  private buildLateral(): void {
    if (this.lateral.length === 0) return;
    this.unitBox ??= new THREE.BoxGeometry(1, 1, 1);
    this.lateralMat ??= new THREE.MeshBasicMaterial({
      color: new THREE.Color(memberColor("lateral")),
      wireframe: true,
      transparent: true,
      opacity: 0.6,
    });
    for (const lat of [...this.lateral].sort((a, b) => a.id.localeCompare(b.id))) {
      const w = Math.abs(lat.max_x_m - lat.min_x_m);
      const d = Math.abs(lat.max_y_m - lat.min_y_m);
      const h = Math.abs(lat.top_z_m - lat.base_z_m);
      if (!(w > 0 && d > 0 && h > 0)) continue;
      const mesh = new THREE.Mesh(this.unitBox, this.lateralMat);
      mesh.name = `lateral:${lat.id}`;
      const [x, y, z] = planToThree([
        (lat.min_x_m + lat.max_x_m) / 2,
        (lat.min_y_m + lat.max_y_m) / 2,
        lat.base_z_m + h / 2,
      ]);
      mesh.position.set(x, y, z);
      mesh.scale.set(w, h, d);
      this.add(mesh);
    }
  }

  private cachedLineMaterial(role: string): THREE.LineBasicMaterial {
    let mat = this.lineMatCache.get(role);
    if (!mat) {
      mat = new THREE.LineBasicMaterial({
        color: new THREE.Color(memberColor(role)),
      });
      this.lineMatCache.set(role, mat);
    }
    return mat;
  }

  private cachedSolidMaterial(kind: string): THREE.MeshStandardMaterial {
    let mat = this.solidMatCache.get(kind);
    if (!mat) {
      mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(memberColor(kind)),
        metalness: 0.35,
        roughness: 0.6,
      });
      this.solidMatCache.set(kind, mat);
    }
    return mat;
  }

  private add(mesh: THREE.LineSegments | THREE.InstancedMesh | THREE.Mesh): void {
    this.built.push(mesh);
    this.group.add(mesh);
  }

  private clear(): void {
    for (const mesh of this.built) {
      this.group.remove(mesh);
      if (mesh instanceof THREE.LineSegments) {
        // Merged line geometry is unique per rebuild.
        mesh.geometry.dispose();
      } else if (mesh instanceof THREE.InstancedMesh) {
        mesh.dispose(); // frees the instance buffers, not the shared geometry
      }
      // Plain lateral Meshes share `unitBox` + `lateralMat`; nothing to free.
    }
    this.built = [];
  }

  dispose(): void {
    this.clear();
    for (const mat of this.lineMatCache.values()) mat.dispose();
    this.lineMatCache.clear();
    for (const mat of this.solidMatCache.values()) mat.dispose();
    this.solidMatCache.clear();
    this.lateralMat?.dispose();
    this.lateralMat = null;
    this.unitBox?.dispose();
    this.unitBox = null;
    this.columns = [];
    this.beams = [];
    this.lateral = [];
  }
}
